<template>
  <div
    class="ca-bullet-container"
    :class="{ 'ca-bullet-container--simplified': isSimplifiedMenu }"
    ref="containerRef"
  >
    <header
      ref="headerRef"
      class="ca-header p-size-4 mt-size-4 gap-size-4 rounded-m bg-white flex fixed items-center"
      :class="{
        'menu-open':
          hoveredIndex !== null || mobileMenuOpen || searchResultsOpen,
        'is-simplified': isSimplifiedMenu,
        'is-hidden': isHidden,
        'has-alert-banner': hasAlertBanner,
      }"
      :style="
        headerMarginTop !== null ? { marginTop: `${headerMarginTop}px` } : null
      "
    >
      <CaHeaderLogo
        :link="model?.menuLogo?.link"
        :imageUrl="model?.menuLogo?.imageUrl"
        :imageSecondaryUrl="model?.menuLogo?.imageSecondaryUrl"
        :imageSecondarySmallUrl="model?.menuLogo?.imageSecondarySmallUrl"
        :visible="!(searchOpen && isMobileOrTablet)"
        :isSimplifiedMenu="isSimplifiedMenu"
      />

      <CaHeaderSearchMegaMenu
        :open="searchResultsOpen && searchMenuReady"
        :searchBarStyle="searchBarStyle"
        :isMobileOrTablet="isMobileOrTablet"
        :autocompleteLoading="autocompleteLoading"
        :searchQuery="searchQuery"
        :suggestions="pesquisaFolder"
        :autocompleteResults="autocompleteResults"
        :highlightMatchedText="highlightMatchedText"
        @select-suggestion="selectSuggestion"
        @show-all="showAllResults"
      />

      <CaHeaderNavButtons
        :items="model?.homeItems"
        :simplifiedItems="model?.homeItems?.[0]?.categories"
        :hoveredIndex="hoveredIndex"
        :isLoading="isLoading"
        :isSimplifiedMenu="isSimplifiedMenu"
        :searchOpen="searchOpen"
        :currentPath="currentPath"
        @navigate="navigateToItem"
        @open-menu="openMenu"
        @close-menu="closeMenu"
        @handle-enter="handleEnter"
        @navigate-category="navigateToCategory"
      />

      <CaSimplifiedNavBar
        v-if="isSimplifiedMenu"
        context="header"
        :items="model?.homeItems?.[0]?.categories"
        :currentPath="currentPath"
        :isLoading="isLoading"
        @navigate-category="navigateToCategory"
      />

      <CaHeaderActions
        ref="actionsRef"
        :isSimplifiedMenu="isSimplifiedMenu"
        :isSearchResultsPage="isSearchResultsPage"
        :searchOpen="searchOpen"
        :searchBarOpen="searchBarOpen"
        :searchQuery="searchQuery"
        :mobileMenuOpen="mobileMenuOpen"
        :isCollapsing="isCollapsing"
        :isMobileOrTablet="isMobileOrTablet"
        :isLoading="isLoading"
        :abrirContaTitle="abrirContaBtn?.title"
        :abrirContaUrl="abrirContaBtn?.url"
        :homebankingTitle="homebankingBtn?.title"
        :homebankingUrl="homebankingBtn?.url"
        @toggle-mobile-menu="toggleMobileMenu"
        @abrir-conta="goToAbrirConta"
        @homebanking="goToHomebanking"
        @open-search="openSearch"
        @close-search="closeSearch"
        @clear-search="clearSearch"
        @open-mobile-search="openMobileSearch"
        @open-search-results="openSearchResults"
        @submit-search="submitSearch"
        @update:searchQuery="searchQuery = $event"
      />

      <CaHeaderMegaMenu
        v-if="!isSimplifiedMenu"
        :homeItems="model?.homeItems"
        :hoveredIndex="hoveredIndex"
        :isLoading="isLoading"
        :isRestoringState="isRestoringState"
        @keep-open="keepOpen"
        @close-menu="closeMenu"
      />

      <Teleport to="body">
        <CaHeaderMobileMenu
          :open="mobileMenuOpen"
          :isSimplifiedMenu="isSimplifiedMenu"
          :homeItems="model?.homeItems"
          :simplifiedItems="model?.homeItems?.[0]?.categories"
          :activeIndex="activeIndex"
          :expandedAccordions="expandedAccordions"
          :currentPath="currentPath"
          :logoLink="model?.menuLogo?.link"
          :logoImageUrl="model?.menuLogo?.imageUrl"
          :homebankingTitle="homebankingBtn?.title"
          :abrirContaTitle="abrirContaBtn?.title"
          :abrirContaUrl="abrirContaBtn?.url"
          @close="toggleMobileMenu"
          @select-tab="selectTabAndScroll"
          @navigate-category="navigateToCategory"
          @toggle-accordion="toggleAccordion"
          @homebanking="goToHomebanking"
          @abrir-conta="goToAbrirConta"
        />
      </Teleport>
      <Teleport to="body">
        <CaHeaderMobileSearch
          :open="mobileSearchOpen"
          :closing="mobileSearchClosing"
          :searchQuery="searchQuery"
          :searchResultsOpen="searchResultsOpen"
          :autocompleteLoading="autocompleteLoading"
          :suggestions="pesquisaFolder"
          :autocompleteResults="autocompleteResults"
          :highlightMatchedText="highlightMatchedText"
          @close="closeMobileSearch"
          @clear-search="clearSearch"
          @submit-search="submitSearch"
          @open-search-results="openSearchResults"
          @select-suggestion="selectSuggestion"
          @show-all="showAllResults"
          @update:searchQuery="searchQuery = $event"
        />
      </Teleport>
    </header>

    <CaSimplifiedNavBar
      v-if="isSimplifiedMenu"
      context="outside"
      :items="model?.homeItems?.[0]?.categories"
      :currentPath="currentPath"
      :isLoading="isLoading"
      @navigate-category="navigateToCategory"
    />
  </div>
</template>
<script>
const isDev = import.meta.env.DEV;
</script>
<script setup>
import { ref, computed, nextTick, onMounted, onUnmounted, watch } from "vue";

import CaHeaderLogo from "./CaHeaderLogo.vue";
import CaHeaderNavButtons from "./CaHeaderNavButtons.vue";
import CaHeaderActions from "./CaHeaderActions.vue";
import CaHeaderMegaMenu from "./CaHeaderMegaMenu.vue";
import CaHeaderSearchMegaMenu from "./CaHeaderSearchMegaMenu.vue";
import CaHeaderMobileMenu from "./CaHeaderMobileMenu.vue";
import CaHeaderMobileSearch from "./CaHeaderMobileSearch.vue";
import CaSimplifiedNavBar from "./CaSimplifiedNavBar.vue";

const props = defineProps({
  model: {
    type: Object,
    default: null,
  },
});

// Para import dinâmico em dev, evitando incluir o mock no bundle de produção
const modelRef = ref(props.model ?? null);
const model = computed(() => modelRef.value || {});

// ── Computed ─────────────────────────────────────────────────────────────────

const currentPath = computed(() => window.location.pathname);

const homebankingBtn = computed(() =>
  model?.value.topRightButtons?.find((b) => b.isHomebankingBtn === true),
);

const abrirContaBtn = computed(() =>
  model?.value.topRightButtons?.find((b) => b.isHomebankingBtn === false),
);

const pesquisaFolder = computed(() => {
  const raw = model.value?.pesquisaFolder;
  let items = [];
  if (Array.isArray(raw)) {
    items = raw[0]?.sugestaoPesquisaItems || raw;
  } else {
    items = raw?.sugestaoPesquisaItems || [];
  }
  return items.filter((item) => item.url && item.url.trim().length > 0);
});

const isSimplifiedMenu = computed(
  () => !homebankingBtn.value && !!abrirContaBtn.value,
);

const isSearchResultsPage = computed(() => {
  const url = new URL(window.location.href);
  return url.pathname.includes("/pesquisa") && url.searchParams.has("phrase");
});

// ── State ─────────────────────────────────────────────────────────────────────

const activeIndex = ref(0);
const hoveredIndex = ref(null);
const mobileMenuOpen = ref(false);
const expandedAccordions = ref({});
const isLoading = ref(true);
const isRestoringState = ref(false);
const searchOpen = ref(false);
const searchBarOpen = ref(false);
const searchClosing = ref(false);
const searchQuery = ref("");
const searchResultsOpen = ref(false);
const isMobile = ref(false);
const isMobileOrTablet = ref(false);
const headerRef = ref(null);
const containerRef = ref(null);
const isCollapsing = ref(false);
const mobileSearchOpen = ref(false);
const mobileSearchClosing = ref(false);
const autocompleteResults = ref([]);
const autocompleteLoading = ref(false);
let autocompleteDebounce = null;
let isClosing = false;

// ── Sticky header ─────────────────────────────────────────────────────────────

const isHidden = ref(false);
const hasAlertBanner = ref(false);
const headerMarginTop = ref(null);
let alertBannerObserver = null;
let baseMarginTop = 0;

const updateHeaderMargin = () => {
  // Sem banner ou com o header escondido, deixa o CSS controlar a margem
  if (!hasAlertBanner.value || isHidden.value) {
    headerMarginTop.value = null;
    return;
  }

  const banner = document.querySelector(".alert");
  if (!banner) {
    headerMarginTop.value = null;
    return;
  }

  const rect = banner.getBoundingClientRect();
  const bannerHeight = rect.height;
  // Parte do banner ainda visivel no topo (bannerHeight no topo, 0 quando sai)
  const visibleBottom = Math.min(Math.max(rect.bottom, 0), bannerHeight);
  // No topo: margem normal; ao afastar do topo: margem menor
  headerMarginTop.value = baseMarginTop - (bannerHeight - visibleBottom);
};

const checkAlertBanner = () => {
  hasAlertBanner.value = !!document.querySelector(".alert");
  updateHeaderMargin();
};

const isMenuOpen = computed(
  () =>
    isMobileOrTablet.value &&
    (hoveredIndex.value !== null ||
      mobileMenuOpen.value ||
      searchResultsOpen.value),
);

let lastScrollY = 0;
let ticking = false;

const handleScroll = () => {
  if (ticking) return;

  window.requestAnimationFrame(() => {
    const currentScrollY =
      window.visualViewport && typeof window.visualViewport.pageTop === "number"
        ? window.visualViewport.pageTop
        : window.scrollY;
    const scrollingUp = currentScrollY < lastScrollY;

    if (currentScrollY <= 20) {
      isHidden.value = false;
    } else if (!scrollingUp) {
      isHidden.value = true;
    } else {
      isHidden.value = false;
    }

    lastScrollY = currentScrollY;
    updateHeaderMargin();
    ticking = false;
  });

  ticking = true;
};

// ── Lifecycle ─────────────────────────────────────────────────────────────────

const actionsRef = ref(null);
const searchBarStyle = ref({});
const searchMenuReady = ref(false);

watch(searchBarOpen, (isOpen) => {
  if (!isOpen) {
    searchMenuReady.value = false;
    searchBarStyle.value = {};
    return;
  }

  searchMenuReady.value = false;
  setTimeout(() => {
    const searchBar = actionsRef.value?.searchBarRef;
    if (!searchBar) return;

    const barRect = searchBar.getBoundingClientRect();
    const headerRect = headerRef.value?.getBoundingClientRect();
    if (!headerRect) return;

    searchBarStyle.value = {
      "--search-bar-left": `${Math.round(barRect.left - headerRect.left)}px`,
      "--search-bar-width": `${Math.round(barRect.width)}px`,
    };

    searchMenuReady.value = true;
  }, 920);
});

const updateScreen = () => {
  isMobile.value = window.innerWidth <= 767;
  isMobileOrTablet.value = window.innerWidth <= 1163;
  updateHeaderMargin();
};

onMounted(async () => {
  // Em desenvolvimento, se não receber um model via props, carrega o mock para facilitar testes
  if (isDev && !modelRef.value) {
    const { mockMenu } = await import("./mocks/headerMocks.js");
    modelRef.value = mockMenu;
  }
  isLoading.value = false;
  document.addEventListener("mousedown", handleClickOutsideSearch);
  document.addEventListener("keydown", handleKeyDown);
  updateScreen();
  window.addEventListener("resize", updateScreen);
  lastScrollY =
    window.visualViewport && typeof window.visualViewport.pageTop === "number"
      ? window.visualViewport.pageTop
      : window.scrollY;
  window.addEventListener("scroll", handleScroll, { passive: true });
  if (window.visualViewport) {
    window.visualViewport.addEventListener("scroll", handleScroll, {
      passive: true,
    });
    window.visualViewport.addEventListener("resize", handleScroll);
  }
  document.addEventListener("touchmove", handleScroll, { passive: true });

  if (headerRef.value) {
    baseMarginTop =
      parseFloat(getComputedStyle(headerRef.value).marginTop) || 0;
  }
  checkAlertBanner();
  alertBannerObserver = new MutationObserver(checkAlertBanner);
  alertBannerObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });

  const url = new URL(window.location.href);

  if (isSearchResultsPage.value) {
    isClosing = true;
    searchOpen.value = false;
    searchBarOpen.value = false;
    searchResultsOpen.value = false;
    searchQuery.value = "";
    autocompleteResults.value = [];
    mobileSearchOpen.value = false;
    mobileSearchClosing.value = false;
    document.body.style.overflow = "";
    nextTick(() => {
      isClosing = false;
    });
    return;
  }

  if (url.searchParams.has("phrase")) {
    const phrase = url.searchParams.get("phrase") || "";
    if (phrase) {
      searchOpen.value = true;
      searchBarOpen.value = true;
      searchQuery.value = phrase;
    } else {
      window.history.pushState("", "", url.pathname);
    }
  }
});

onUnmounted(() => {
  document.removeEventListener("mousedown", handleClickOutsideSearch);
  document.removeEventListener("keydown", handleKeyDown);
  window.removeEventListener("resize", updateScreen);
  window.removeEventListener("scroll", handleScroll);
  if (window.visualViewport) {
    window.visualViewport.removeEventListener("scroll", handleScroll);
    window.visualViewport.removeEventListener("resize", handleScroll);
  }
  document.removeEventListener("touchmove", handleScroll);
  alertBannerObserver?.disconnect();
  clearTimeout(autocompleteDebounce);
  document.body.style.overflow = "";
});

// ── Watchers ─────────────────────────────────────────────────────────────────

watch(
  () => model.value,
  (newValue) => {
    if (newValue?.homeItems && newValue.homeItems.length > 0) {
      isLoading.value = false;
    }
  },
);

// Esconde o alert banner temporariamente enquanto o menu esta aberto
watch(isMenuOpen, (open) => {
  const banner = document.querySelector(".alert");
  if (banner) banner.style.display = open ? "none" : "";
  updateHeaderMargin();
});

watch(searchQuery, (val) => {
  if (isClosing) return;

  const trimmed = val?.trim() || "";
  if (trimmed.length < 3) {
    autocompleteResults.value = [];
    searchResultsOpen.value =
      !!(searchOpen.value || mobileSearchOpen.value) &&
      pesquisaFolder.value.length > 0;
    return;
  }

  searchResultsOpen.value = true;
  fetchAutocomplete(trimmed);
});

// ── Accent normalization ──────────────────────────────────────────────────────

const accentMap = {
  á: "a",
  Ý: "a",
  à: "a",
  À: "a",
  â: "a",
  Â: "a",
  ã: "a",
  Ã: "a",
  é: "e",
  É: "e",
  ê: "e",
  Ê: "e",
  í: "i",
  Ý: "i",
  ó: "o",
  Ó: "o",
  õ: "o",
  Õ: "o",
  ô: "o",
  Ô: "o",
  ç: "c",
  Ç: "c",
};

const normalize = (term) =>
  term
    .split("")
    .map((ch) => accentMap[ch] || ch)
    .join("");

// ── Highlight Search Text ─────────────────────────────────────────────────────

const highlightMatchedText = (label, searchTerm) => {
  if (!searchTerm || searchTerm.length < 3)
    return { matched: "", unmatched: label };

  const normLabel = normalize(label.toLowerCase());
  const normTerm = normalize(searchTerm.toLowerCase());
  const indexOf = normLabel.indexOf(normTerm);

  if (indexOf === -1) return { matched: "", unmatched: label };

  const matched = label.substring(indexOf, indexOf + searchTerm.length);
  const unmatched = label.substring(indexOf + searchTerm.length);
  return { matched, unmatched };
};

// ── Search URL ────────────────────────────────────────────────────────────────

const buildSearchUrl = (phrase) => {
  phrase = phrase ? phrase.trim() : "";
  if (phrase.length > 100) phrase = phrase.substring(0, 100);

  const url = new URL(window.location.href);
  const base = `${url.protocol}//${url.host}/pesquisa`;

  return phrase ? `${base}?phrase=${encodeURIComponent(phrase)}` : base;
};

// ── Autocomplete Search ───────────────────────────────────────────────────────

const fetchAutocomplete = (term) => {
  clearTimeout(autocompleteDebounce);

  if (!term || term.length < 3) {
    autocompleteResults.value = [];
    searchResultsOpen.value =
      !!(searchOpen.value || mobileSearchOpen.value) &&
      pesquisaFolder.value.length > 0;
    return;
  }

  autocompleteDebounce = setTimeout(async () => {
    autocompleteLoading.value = true;
    searchResultsOpen.value = true;

    try {
      const formData = new FormData();
      formData.append("phrase", term);

      const res = await fetch("/api/Sitecore/Indexing/AutoCompleteSearch", {
        method: "POST",
        credentials: "same-origin",
        headers: { "X-Requested-With": "XMLHttpRequest" },
        body: formData,
      });

      const data = await res.json();
      const items = Array.isArray(data) ? data : [];
      const normalized = items.map((item) => {
        if (typeof item === "string") return { label: item, value: item };
        return {
          label:
            item.label ||
            item.value ||
            item.text ||
            item.name ||
            JSON.stringify(item),
          value:
            item.value ||
            item.label ||
            item.text ||
            item.name ||
            JSON.stringify(item),
        };
      });

      const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const matcher = new RegExp(escaped, "i");

      autocompleteResults.value = normalized.filter(
        (item) =>
          matcher.test(item.label) || matcher.test(normalize(item.label)),
      );
    } catch {
      autocompleteResults.value = [];
    } finally {
      autocompleteLoading.value = false;
    }
  }, 300);
};

// ── Suggestion selection ──────────────────────────────────────────────────────

const selectSuggestion = (suggestion) => {
  if (suggestion?.url && `${suggestion.url}`.trim().length > 0) {
    window.location.href = suggestion.url;
    return;
  }

  const value = [
    suggestion?.value,
    suggestion?.label,
    suggestion?.title,
    suggestion,
  ]
    .find((v) => v !== undefined && v !== null && `${v}`.trim().length > 0)
    ?.toString()
    .trim();

  if (value) window.location.href = buildSearchUrl(value);
};

// ── Submit search ─────────────────────────────────────────────────────────────

const submitSearch = () => {
  const trimmed = searchQuery.value.trim();
  if (trimmed.length >= 3 && trimmed.length <= 100) {
    searchResultsOpen.value = false;
    autocompleteResults.value = [];
    const url = buildSearchUrl(trimmed);
    searchQuery.value = "";
    window.location.href = url;
  }
};

// ── Desktop search open/close ─────────────────────────────────────────────────

const openSearch = () => {
  searchClosing.value = false;
  searchOpen.value = true;
  searchBarOpen.value = true;
  autocompleteResults.value = [];
  searchResultsOpen.value =
    !searchQuery.value && pesquisaFolder.value.length > 0;
};

const closeSearch = () => {
  isClosing = true;
  searchClosing.value = true;
  searchQuery.value = "";
  searchResultsOpen.value = false;
  autocompleteResults.value = [];
  searchBarOpen.value = false;
  setTimeout(() => {
    searchOpen.value = false;
    searchClosing.value = false;
    isClosing = false;
  }, 900);
};

const clearSearch = () => {
  searchQuery.value = "";
  autocompleteResults.value = [];
  searchResultsOpen.value = pesquisaFolder.value.length > 0;
};

const openSearchResults = () => {
  const trimmed = searchQuery.value?.trim() || "";
  if (trimmed.length < 3 && pesquisaFolder.value.length) {
    searchResultsOpen.value = true;
    return;
  }
  if (trimmed.length >= 3) searchResultsOpen.value = true;
};

// ── Mobile search open/close ──────────────────────────────────────────────────

const openMobileSearch = () => {
  mobileSearchClosing.value = false;
  mobileSearchOpen.value = true;
  document.body.style.overflow = "hidden";
  autocompleteResults.value = [];
  searchResultsOpen.value = pesquisaFolder.value.length > 0;
};

const closeMobileSearch = () => {
  mobileSearchClosing.value = true;
  searchQuery.value = "";
  searchResultsOpen.value = false;
  autocompleteResults.value = [];
  setTimeout(() => {
    mobileSearchOpen.value = false;
    mobileSearchClosing.value = false;
    document.body.style.overflow = "";
  }, 350);
};

const closeMobileSearchInstant = () => {
  isClosing = true;
  mobileSearchOpen.value = false;
  mobileSearchClosing.value = false;
  searchResultsOpen.value = false;
  searchOpen.value = false;
  searchBarOpen.value = false;
  autocompleteResults.value = [];
  document.body.style.overflow = "";
  nextTick(() => {
    isClosing = false;
  });
};

const handleClickOutsideSearch = (event) => {
  const clickedInsideSearch = event.target.closest(".js-search-area");
  if (!clickedInsideSearch) {
    if (searchBarOpen.value && !isMobileOrTablet.value) closeSearch();
    if (!mobileSearchOpen.value) searchResultsOpen.value = false;
  }
};

// ── Mega Menu ─────────────────────────────────────────────────────────────────

let closeTimeout = null;

const openMenu = (idx) => {
  clearTimeout(closeTimeout);
  // So abre o megamenu se o item tiver categorias
  const item = model.value?.homeItems?.[idx];
  if (!item?.categories?.length) {
    hoveredIndex.value = null;
    return;
  }
  hoveredIndex.value = idx;
};

const closeMenu = () => {
  closeTimeout = setTimeout(() => {
    hoveredIndex.value = null;
  }, 200);
};

const keepOpen = () => {
  clearTimeout(closeTimeout);
};

const handleEnter = (idx) => {
  if (isSimplifiedMenu.value) {
    const item = model.value?.homeItems?.[0]?.categories?.[idx];
    navigateToCategory(item);
  } else {
    openMenu(idx);
  }
};

const handleKeyDown = (event) => {
  if (event.key === "Escape") {
    if (mobileSearchOpen.value) closeMobileSearch();
    else if (searchBarOpen.value) closeSearch();
    hoveredIndex.value = null;
  }
};

// ── Accordion ─────────────────────────────────────────────────────────────────

const toggleAccordion = (sectionIdx) => {
  const key = `${activeIndex.value}-${sectionIdx}`;
  expandedAccordions.value[key] = !expandedAccordions.value[key];
};

const selectTab = (tabIdx) => {
  activeIndex.value = tabIdx;
  expandedAccordions.value = {};
};

const selectTabAndScroll = (tabIdx, event) => {
  selectTab(tabIdx);
  nextTick(() => {
    const button = event?.target?.closest?.(".ca-header__button");
    if (button?.parentElement) {
      button.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }
  });
};

const getMobileActiveTabIndex = () => {
  if (!Array.isArray(model.value?.homeItems)) return 0;
  const index = model.value.homeItems.findIndex((item) => {
    const itemUrl = item?.url?.toString().trim();
    if (!itemUrl) return false;
    return (
      currentPath.value === itemUrl ||
      currentPath.value.startsWith(`${itemUrl}/`)
    );
  });
  return index >= 0 ? index : 0;
};

const toggleMobileMenu = () => {
  if (isCollapsing.value) return;
  isCollapsing.value = true;

  if (!mobileMenuOpen.value) {
    activeIndex.value = getMobileActiveTabIndex();
    setTimeout(() => {
      mobileMenuOpen.value = true;
      isCollapsing.value = false;
    }, 300);
  } else {
    mobileMenuOpen.value = false;
    setTimeout(() => {
      isCollapsing.value = false;
      activeIndex.value = 0;
      expandedAccordions.value = {};
    }, 300);
  }
};

// ── Navigation ────────────────────────────────────────────────────────────────

const goToAbrirConta = () => {
  const url = abrirContaBtn.value?.url;
  if (url) window.location.href = url;
};

const goToHomebanking = () => {
  const url = homebankingBtn.value?.url;
  if (url) window.location.href = url;
};

const navigateToItem = (idx) => {
  const item = model.value?.homeItems?.[idx];
  activeIndex.value = idx;
  if (item?.url) window.location.href = item.url;
};

const navigateToCategory = (item) => {
  if (item?.url) window.location.href = item.url;
};

// ── Show all search results ───────────────────────────────────────────────────

const showAllResults = () => {
  const phrase = searchQuery.value.trim();
  searchResultsOpen.value = false;
  autocompleteResults.value = [];
  clearTimeout(autocompleteDebounce);

  if (isMobileOrTablet.value) {
    // mobile: close instantly and navigate immediately
    closeMobileSearchInstant();
    if (phrase.length >= 3 && phrase.length <= 100) {
      window.location.assign(buildSearchUrl(phrase));
    }
    return;
  }

  // desktop: use the same close animation as clicking outside
  closeSearch();

  // navigate after the close animation completes (matches closeSearch timeout)
  if (phrase.length >= 3 && phrase.length <= 100) {
    setTimeout(() => {
      window.location.assign(buildSearchUrl(phrase));
    }, 900);
  }
};
</script>

<style scoped lang="scss">
.ca-bullet-container {
  --ca-margin: #{ca-margin("base")};

  @include ca-bp("sm") {
    --ca-margin: #{ca-margin("sm")};
  }
  @include ca-bp("md") {
    --ca-margin: #{ca-margin("md")};
  }
  @include ca-bp("lg") {
    --ca-margin: #{ca-margin("lg")};
  }
  @include ca-bp("lg2") {
    --ca-margin: #{ca-margin("lg2")};
  }
  @include ca-bp("xl") {
    --ca-margin: #{ca-margin("xl")};
  }

  &--simplified {
    @media (max-width: 767px) {
      --ca-margin: 0px;
      background: white;
      max-width: unset;
    }
  }
}

.ca-header {
  box-shadow: 0 2px 6px 0 rgba(0, 0, 0, 0.1);
  width: min(
    calc(100% - var(--ca-margin) * 2),
    calc(1920px - var(--ca-margin) * 2)
  );
  left: 50%;
  transform: translateX(-50%) translateY(0);
  -webkit-tap-highlight-color: transparent;
  transition:
    transform 0.45s cubic-bezier(0.22, 1, 0.36, 1),
    box-shadow 0.3s ease;
  will-change: transform;
  z-index: 9998;
  max-width: 1920px;

  * {
    -webkit-tap-highlight-color: transparent;
  }

  &.is-hidden {
    transform: translateX(-50%) translateY(-140%);
    transition:
      transform 0.55s cubic-bezier(0.65, 0, 0.35, 1),
      box-shadow 0.3s ease;
  }

  &.has-alert-banner.is-hidden {
    margin-top: -110px;
  }
}

@media (max-width: 1024px) {
  .ca-header {
    padding-top: var(--size-4);
    padding-bottom: var(--size-4);
    flex-wrap: wrap;
  }
}

// Drop the shadow on tablet/mobile only while the menu/search is open
// (1163px matches the header's isMobileOrTablet boundary).
@media (max-width: 1163px) {
  .ca-header.menu-open {
    box-shadow: none;
  }
}

@media (max-width: 767px) {
  .ca-header {
    padding-top: var(--size-4);
    padding-bottom: var(--size-4);
  }

  .ca-header.is-simplified {
    box-shadow: none;
    border-radius: var(--size-0);
    margin-top: var(--size-0);
    padding: var(--size-6) var(--size-0);
    position: relative;
  }
}

@media (min-width: 1024px) and (max-width: 1153px) {
  .ca-header {
    gap: var(--size-3);
  }
}

input:-webkit-autofill {
  -webkit-box-shadow: 0 0 0 1000px white inset;
}
</style>
